import * as XLSX from "xlsx";
import { generateId } from "../shared/id";
import { formToTripSavePayload } from "./tripForm.model";
import {
  importTripsFromCsvFile,
  triggerCsvDownload,
  type CsvImportResult,
} from "./csv";
import {
  loadFullLocalData,
  loadTrips,
  markBackupCompleted,
  saveTripWithRecords,
} from "./storage";
import {
  BOOKING_TYPES,
  EXPENSE_CATEGORIES,
  PAYMENT_MODES,
  TRIP_STATUSES,
  VEHICLE_SOURCES,
  type BookingType,
  type ExpenseCategory,
  type ExpenseFormRow,
  type ExpenseRecord,
  type PaymentFormRow,
  type PaymentMode,
  type PaymentRecord,
  type Trip,
  type TripFormData,
  type TripStatus,
  type VehicleSource,
} from "./types";

export const TRIPS_SHEET = "Trips";
export const PAYMENTS_SHEET = "Payments";
export const EXPENSES_SHEET = "Expenses";

export const TRIP_WORKBOOK_COLUMNS = [
  "Trip Id",
  "Date",
  "Vehicle No",
  "Party Name",
  "Hire Amount",
  "Driver Name",
  "Driver Mobile",
  "Origin",
  "Destination",
  "Booking Type",
  "Vehicle Source",
  "Broker Name",
  "Trip Status",
  "Notes",
] as const;

export const PAYMENT_WORKBOOK_COLUMNS = [
  "Trip Id",
  "Payment Date",
  "Payment Amount",
  "Payment Mode",
  "Payment Note",
] as const;

export const EXPENSE_WORKBOOK_COLUMNS = [
  "Trip Id",
  "Expense Date",
  "Expense Category",
  "Expense Amount",
  "Expense Note",
] as const;

type WorkbookRow = Record<string, unknown>;

type WorkbookImportResult = CsvImportResult;

export type WorkbookFileResult = {
  file: File;
  recordCount: number;
  hasTrips: boolean;
};

const WORKBOOK_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const asText = (value: unknown): string => String(value ?? "").trim();

const toNumber = (value: unknown): number => {
  const parsed = Number(String(value ?? "").replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : 0;
};

const isKnownValue = <T extends readonly string[]>(
  values: T,
  value: string
): value is T[number] => !value || values.includes(value);

const formatDateValue = (value: unknown): string => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  if (typeof value === "number") {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) {
      const month = String(parsed.m).padStart(2, "0");
      const day = String(parsed.d).padStart(2, "0");
      return `${parsed.y}-${month}-${day}`;
    }
  }

  const text = asText(value);
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);

  const dateMatch = text.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dateMatch) {
    const [, day, month, year] = dateMatch;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  return text;
};

export const tripsToWorkbookRows = (trips: Trip[]): WorkbookRow[] =>
  trips.map((trip) => ({
    "Trip Id": trip.id,
    Date: trip.date,
    "Vehicle No": trip.vehicleNo,
    "Party Name": trip.partyName,
    "Hire Amount": trip.hireAmount,
    "Driver Name": trip.driverName,
    "Driver Mobile": trip.driverMobile,
    Origin: trip.origin,
    Destination: trip.destination,
    "Booking Type": trip.bookingType,
    "Vehicle Source": trip.vehicleSource,
    "Broker Name": trip.brokerName,
    "Trip Status": trip.status,
    Notes: trip.notes,
  }));

export const paymentsToWorkbookRows = (
  payments: PaymentRecord[]
): WorkbookRow[] =>
  payments.map((payment) => ({
    "Trip Id": payment.tripId,
    "Payment Date": payment.date,
    "Payment Amount": payment.amount,
    "Payment Mode": payment.mode,
    "Payment Note": payment.note,
  }));

export const expensesToWorkbookRows = (
  expenses: ExpenseRecord[]
): WorkbookRow[] =>
  expenses.map((expense) => ({
    "Trip Id": expense.tripId,
    "Expense Date": expense.date,
    "Expense Category": expense.category,
    "Expense Amount": expense.amount,
    "Expense Note": expense.note,
  }));

const sheetFromRows = (
  rows: WorkbookRow[],
  headers: readonly string[]
): XLSX.WorkSheet => XLSX.utils.json_to_sheet(rows, { header: [...headers] });

export const createTripWorkbook = ({
  trips,
  payments,
  expenses,
}: {
  trips: Trip[];
  payments: PaymentRecord[];
  expenses: ExpenseRecord[];
}): XLSX.WorkBook => {
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    sheetFromRows(tripsToWorkbookRows(trips), TRIP_WORKBOOK_COLUMNS),
    TRIPS_SHEET
  );
  XLSX.utils.book_append_sheet(
    workbook,
    sheetFromRows(paymentsToWorkbookRows(payments), PAYMENT_WORKBOOK_COLUMNS),
    PAYMENTS_SHEET
  );
  XLSX.utils.book_append_sheet(
    workbook,
    sheetFromRows(expensesToWorkbookRows(expenses), EXPENSE_WORKBOOK_COLUMNS),
    EXPENSES_SHEET
  );
  return workbook;
};

export const withFallbackWorkbookLineItems = ({
  trips,
  payments,
  expenses,
}: {
  trips: Trip[];
  payments: PaymentRecord[];
  expenses: ExpenseRecord[];
}): {
  trips: Trip[];
  payments: PaymentRecord[];
  expenses: ExpenseRecord[];
} => {
  const paymentTripIds = new Set(payments.map((payment) => payment.tripId));
  const expenseTripIds = new Set(expenses.map((expense) => expense.tripId));

  const fallbackPayments: PaymentRecord[] = trips.flatMap((trip) =>
    !paymentTripIds.has(trip.id) && trip.advanceReceived > 0
      ? [
          {
            id: `${trip.id}-payment-export-advance`,
            tripId: trip.id,
            date: trip.date,
            amount: trip.advanceReceived,
            mode: "Cash",
            note: "Advance received",
          },
        ]
      : []
  );

  const fallbackExpenses: ExpenseRecord[] = trips.flatMap((trip) => {
    if (expenseTripIds.has(trip.id)) return [];

    return [
      { key: "fuel", category: "Fuel" as const, amount: trip.fuelExp },
      { key: "fastag", category: "FASTag" as const, amount: trip.fastagExp },
      {
        key: "driver-bata",
        category: "Driver Bata" as const,
        amount: trip.driverBata,
      },
      {
        key: "commission",
        category: "Commission" as const,
        amount: trip.commissionExp,
      },
      { key: "other", category: "Other" as const, amount: trip.otherExp },
    ]
      .filter((expense) => expense.amount > 0)
      .map((expense) => ({
        id: `${trip.id}-expense-export-${expense.key}`,
        tripId: trip.id,
        date: trip.date,
        category: expense.category,
        amount: expense.amount,
        note: "",
      }));
  });

  return {
    trips,
    payments: [...payments, ...fallbackPayments],
    expenses: [...expenses, ...fallbackExpenses],
  };
};

export const triggerWorkbookDownload = (
  workbook: XLSX.WorkBook,
  filename: string
): void => {
  const data = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([data], { type: WORKBOOK_MIME });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const createTripWorkbookFile = async (): Promise<WorkbookFileResult> => {
  const data = withFallbackWorkbookLineItems(await loadFullLocalData());
  const today = new Date().toISOString().slice(0, 10);
  const hasTrips = data.trips.length > 0;
  const workbook = createTripWorkbook(data);
  const bytes = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const filename = hasTrips
    ? `haulledger-backup-${today}.xlsx`
    : `haulledger-import-format-${today}.xlsx`;

  return {
    file: new File([bytes], filename, { type: WORKBOOK_MIME }),
    recordCount: data.trips.length + data.payments.length + data.expenses.length,
    hasTrips,
  };
};

export const downloadWorkbookFile = (file: File): void => {
  const link = document.createElement("a");
  const url = URL.createObjectURL(file);
  link.href = url;
  link.download = file.name;
  link.click();
  URL.revokeObjectURL(url);
};

export const exportTripWorkbookFile = async (): Promise<number> => {
  const result = await createTripWorkbookFile();
  downloadWorkbookFile(result.file);
  await markBackupCompleted();
  return result.recordCount;
};

export const canShareWorkbookFile = (file: File): boolean => {
  if (
    typeof navigator === "undefined" ||
    typeof navigator.share !== "function" ||
    typeof navigator.canShare !== "function"
  ) {
    return false;
  }

  try {
    return navigator.canShare({
      files: [file],
      title: "HaulLedger backup",
      text: "HaulLedger trips, payments, and expenses backup",
    });
  } catch {
    return false;
  }
};

export const sharePreparedTripWorkbookFile = async (file: File): Promise<void> => {
  const shareData = {
    files: [file],
    title: "HaulLedger backup",
    text: "HaulLedger trips, payments, and expenses backup",
  };

  if (!canShareWorkbookFile(file)) {
    throw new Error("File sharing is unavailable in this browser");
  }

  // This must be the first asynchronous browser action after a direct user tap.
  await navigator.share(shareData);
  await markBackupCompleted();
};

export const downloadPreparedTripWorkbookFile = async (file: File): Promise<void> => {
  downloadWorkbookFile(file);
  await markBackupCompleted();
};

const readRows = (workbook: XLSX.WorkBook, sheetName: string): WorkbookRow[] => {
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) return [];
  return XLSX.utils.sheet_to_json<WorkbookRow>(sheet, {
    defval: "",
    raw: true,
  });
};

const workbookFromFile = async (file: File): Promise<XLSX.WorkBook> => {
  const buffer =
    typeof file.arrayBuffer === "function"
      ? await file.arrayBuffer()
      : await new Promise<ArrayBuffer>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as ArrayBuffer);
          reader.onerror = () => reject(reader.error);
          reader.readAsArrayBuffer(file);
        });
  return XLSX.read(buffer, { type: "array", cellDates: true });
};

type PreparedTrip = {
  sourceTripId: string;
  internalTripId: string;
  form: TripFormData;
};

export const parseTripWorkbook = async (
  workbook: XLSX.WorkBook,
  existingTrips: Trip[] = []
): Promise<{
  trips: PreparedTrip[];
  rejectedCount: number;
  errors: string[];
}> => {
  const rows = readRows(workbook, TRIPS_SHEET);
  const errors: string[] = [];
  const seenSourceIds = new Set<string>();
  const existingById = new Map(existingTrips.map((trip) => [trip.id, trip]));
  const trips: PreparedTrip[] = [];

  rows.forEach((row, index) => {
    const rowNumber = index + 2;
    const sourceTripId = asText(row["Trip Id"]) || `ROW-${rowNumber}`;
    const date = formatDateValue(row.Date);
    const bookingType = asText(row["Booking Type"]) || "Direct";
    const vehicleSource = asText(row["Vehicle Source"]) || "Own Vehicle";
    const status = asText(row["Trip Status"]) || "Planned";

    if (seenSourceIds.has(sourceTripId)) {
      errors.push(`Trips row ${rowNumber}: duplicate Trip Id "${sourceTripId}".`);
      return;
    }
    seenSourceIds.add(sourceTripId);

    if (!date) {
      errors.push(`Trips row ${rowNumber}: Date is required.`);
      return;
    }

    if (!isKnownValue(BOOKING_TYPES, bookingType)) {
      errors.push(`Trips row ${rowNumber}: unknown Booking Type "${bookingType}".`);
      return;
    }

    if (!isKnownValue(VEHICLE_SOURCES, vehicleSource)) {
      errors.push(`Trips row ${rowNumber}: unknown Vehicle Source "${vehicleSource}".`);
      return;
    }

    if (!isKnownValue(TRIP_STATUSES, status)) {
      errors.push(`Trips row ${rowNumber}: unknown Trip Status "${status}".`);
      return;
    }

    const existingTrip = existingById.get(sourceTripId);
    const internalTripId = existingTrip?.id ?? generateId();
    const form: TripFormData = {
      tripNo: existingTrip?.tripNo ?? "",
      date,
      bookingType: bookingType as BookingType,
      vehicleSource: vehicleSource as VehicleSource,
      brokerName: asText(row["Broker Name"]),
      vehicleNo: asText(row["Vehicle No"]),
      driverName: asText(row["Driver Name"]),
      driverMobile: asText(row["Driver Mobile"]),
      partyName: asText(row["Party Name"]),
      origin: asText(row.Origin),
      destination: asText(row.Destination),
      hireAmount: String(toNumber(row["Hire Amount"]) || ""),
      advanceReceived: "",
      fuelExp: "",
      fastagExp: "",
      driverBata: "",
      commissionExp: "",
      otherExp: "",
      status: status as TripStatus,
      notes: asText(row.Notes),
      payments: [],
      expenses: [],
    };

    trips.push({ sourceTripId, internalTripId, form });
  });

  return { trips, rejectedCount: errors.length, errors };
};

export const parsePaymentWorkbookRows = (
  workbook: XLSX.WorkBook,
  tripDateBySourceId: Map<string, string>
): {
  paymentsBySourceTripId: Map<string, PaymentFormRow[]>;
  rejectedCount: number;
  errors: string[];
} => {
  const rows = readRows(workbook, PAYMENTS_SHEET);
  const errors: string[] = [];
  const paymentsBySourceTripId = new Map<string, PaymentFormRow[]>();

  rows.forEach((row, index) => {
    const rowNumber = index + 2;
    const sourceTripId = asText(row["Trip Id"]);
    const amount = toNumber(row["Payment Amount"]);
    const mode = asText(row["Payment Mode"]) || "Cash";

    if (!sourceTripId || !tripDateBySourceId.has(sourceTripId)) {
      errors.push(`Payments row ${rowNumber}: Trip Id must match a Trips sheet row.`);
      return;
    }

    if (amount <= 0) {
      errors.push(`Payments row ${rowNumber}: Payment Amount must be greater than 0.`);
      return;
    }

    if (!isKnownValue(PAYMENT_MODES, mode)) {
      errors.push(`Payments row ${rowNumber}: unknown Payment Mode "${mode}".`);
      return;
    }

    const payment: PaymentFormRow = {
      id: generateId(),
      date:
        formatDateValue(row["Payment Date"]) ||
        tripDateBySourceId.get(sourceTripId) ||
        "",
      amount: String(amount),
      mode: mode as PaymentMode,
      note: asText(row["Payment Note"]),
    };
    paymentsBySourceTripId.set(sourceTripId, [
      ...(paymentsBySourceTripId.get(sourceTripId) ?? []),
      payment,
    ]);
  });

  return {
    paymentsBySourceTripId,
    rejectedCount: errors.length,
    errors,
  };
};

export const parseExpenseWorkbookRows = (
  workbook: XLSX.WorkBook,
  tripDateBySourceId: Map<string, string>
): {
  expensesBySourceTripId: Map<string, ExpenseFormRow[]>;
  rejectedCount: number;
  errors: string[];
} => {
  const rows = readRows(workbook, EXPENSES_SHEET);
  const errors: string[] = [];
  const expensesBySourceTripId = new Map<string, ExpenseFormRow[]>();

  rows.forEach((row, index) => {
    const rowNumber = index + 2;
    const sourceTripId = asText(row["Trip Id"]);
    const amount = toNumber(row["Expense Amount"]);
    const category = asText(row["Expense Category"]) || "Other";

    if (!sourceTripId || !tripDateBySourceId.has(sourceTripId)) {
      errors.push(`Expenses row ${rowNumber}: Trip Id must match a Trips sheet row.`);
      return;
    }

    if (amount <= 0) {
      errors.push(`Expenses row ${rowNumber}: Expense Amount must be greater than 0.`);
      return;
    }

    if (!isKnownValue(EXPENSE_CATEGORIES, category)) {
      errors.push(`Expenses row ${rowNumber}: unknown Expense Category "${category}".`);
      return;
    }

    const expense: ExpenseFormRow = {
      id: generateId(),
      date:
        formatDateValue(row["Expense Date"]) ||
        tripDateBySourceId.get(sourceTripId) ||
        "",
      category: category as ExpenseCategory,
      amount: String(amount),
      note: asText(row["Expense Note"]),
    };
    expensesBySourceTripId.set(sourceTripId, [
      ...(expensesBySourceTripId.get(sourceTripId) ?? []),
      expense,
    ]);
  });

  return {
    expensesBySourceTripId,
    rejectedCount: errors.length,
    errors,
  };
};

export const importTripWorkbookFile = async (
  file: File
): Promise<WorkbookImportResult> => {
  if (file.name.toLowerCase().endsWith(".csv")) {
    return importTripsFromCsvFile(file);
  }

  const workbook = await workbookFromFile(file);
  const existingTrips = await loadTrips();
  const parsedTrips = await parseTripWorkbook(workbook, existingTrips);
  const tripDateBySourceId = new Map(
    parsedTrips.trips.map((trip) => [trip.sourceTripId, trip.form.date])
  );
  const parsedPayments = parsePaymentWorkbookRows(workbook, tripDateBySourceId);
  const parsedExpenses = parseExpenseWorkbookRows(workbook, tripDateBySourceId);

  let importedCount = 0;
  for (const preparedTrip of parsedTrips.trips) {
    const form: TripFormData = {
      ...preparedTrip.form,
      payments:
        parsedPayments.paymentsBySourceTripId.get(preparedTrip.sourceTripId) ??
        [],
      expenses:
        parsedExpenses.expensesBySourceTripId.get(preparedTrip.sourceTripId) ??
        [],
    };
    const payload = formToTripSavePayload(form, preparedTrip.internalTripId);
    await saveTripWithRecords(payload);
    importedCount += 1 + payload.payments.length + payload.expenses.length;
  }

  return {
    importedCount,
    rejectedCount:
      parsedTrips.rejectedCount +
      parsedPayments.rejectedCount +
      parsedExpenses.rejectedCount,
    errors: [
      ...parsedTrips.errors,
      ...parsedPayments.errors,
      ...parsedExpenses.errors,
    ],
    kind: "workbook",
  };
};

export const exportTripFormatCsvFile = (): void => {
  const csv = [
    `Sheet,${TRIP_WORKBOOK_COLUMNS.join(",")}`,
    `Sheet,${PAYMENT_WORKBOOK_COLUMNS.join(",")}`,
    `Sheet,${EXPENSE_WORKBOOK_COLUMNS.join(",")}`,
  ].join("\n");
  triggerCsvDownload(csv, "haulledger-excel-format-columns.csv");
};
